/**
 * Notification tones, synthesised with WebAudio so the bundle carries no audio
 * assets and nothing extra is fetched from the merchant's page.
 *
 * Mute is a client-side preference. Emergency messages ignore it — the server
 * ships `isEmergency` on the payload precisely so the client can override.
 */

let ctx = null;

function context() {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  // Autoplay policy parks the context until a gesture; resume is a no-op when
  // it is already running.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/** One sine blip. `at` is an offset in seconds from now. */
function tone(audio, { freq, at = 0, duration = 0.16, gain = 0.12 }) {
  const start = audio.currentTime + at;
  const osc = audio.createOscillator();
  const amp = audio.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, start);

  // Shaped envelope — a raw gate on a sine clicks audibly.
  amp.gain.setValueAtTime(0.0001, start);
  amp.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(amp).connect(audio.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

/** Soft two-note rise for an ordinary incoming message. */
export function playMessageTone() {
  const audio = context();
  if (!audio) return;
  tone(audio, { freq: 660, at: 0, gain: 0.09 });
  tone(audio, { freq: 880, at: 0.11, gain: 0.09 });
}

/**
 * Emergency: the two-tone chime, repeated three times and noticeably louder.
 * Deliberately harder to ignore than the message tone.
 */
export function playEmergencyTone() {
  const audio = context();
  if (!audio) return;
  for (let i = 0; i < 3; i += 1) {
    const at = i * 0.44;
    tone(audio, { freq: 987.77, at, duration: 0.2, gain: 0.26 });
    tone(audio, { freq: 739.99, at: at + 0.2, duration: 0.22, gain: 0.26 });
  }
}

/**
 * Unlocks audio on the first user gesture. Browsers refuse to start an
 * AudioContext without one, so an emergency arriving before any interaction
 * would otherwise be silent.
 */
export function primeAudio() {
  const audio = context();
  if (!audio) return;
  const buffer = audio.createBuffer(1, 1, 22050);
  const source = audio.createBufferSource();
  source.buffer = buffer;
  source.connect(audio.destination);
  source.start(0);
}

export default { playMessageTone, playEmergencyTone, primeAudio };
